const COPY = {
  add: 'Add to bag',
  adding: 'Adding to your bag…',
  unavailable: 'Unavailable',
  soldOut: 'Sold out',
  from: 'From {price}',
  colorCount: '{count} colors',
  seatOnly: 'Chair only',
  boosterIncluded: 'Matching four-piece booster set',
  boosterUnavailable: 'The matching booster set is unavailable for this fabric.',
  totalUnavailable: 'Total unavailable',
  productCaption: '{fabric} / {color}',
  productAlt: 'Oyster Meditation Chair in {fabric}, {color}',
  roomCaption: 'Shown in a room. The pictured finish may differ from your selection.',
  roomAlt: 'Oyster Meditation Chair in a room',
  photoPosition: 'Photo {index} of {count}',
  sharedCaption: 'Shown in use. The pictured finish may differ from your selection.',
  sharedAlt: 'Oyster Meditation Chair shown in use',
  photoUnavailable: 'This image could not load. Your configuration is still available below.',
  photoFallbackCaption: 'Product image shown. The pictured finish may differ from your selection.',
  photoFallbackAlt: 'Oyster Meditation Chair',
  added: 'Your configuration has been added to your bag.',
  cartError: 'We could not confirm that every item was added. Check your bag before trying again.',
  bag: 'View bag',
};

function make(tag, className, text) {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (text !== undefined) element.textContent = text;
  return element;
}

function imageUrl(value) {
  if (typeof value !== 'string' || !value) return '';
  try {
    const url = new URL(value, window.location.href);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
  } catch {
    return '';
  }
}

if (!customElements.get('oyster-configurator')) {
  customElements.define('oyster-configurator', class extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.events = new AbortController();
      this.initialize(this.events.signal);
    }

    async initialize(signal) {
      try {
        this.config = JSON.parse(this.querySelector('script[data-config]').textContent);
        this.strings = { ...COPY, ...this.config.strings };
        this.core = await import(this.dataset.coreUrl);
        if (signal.aborted) return;

        const colorImages = new Map(this.config.definitions.flatMap((family) => (
          family.colors.map((color) => [color.name, color])
        )));
        const variants = this.config.variants.map((variant) => {
          const image = imageUrl(variant.image);
          const fallback = colorImages.get(variant.title);
          return {
            ...variant,
            image: image || imageUrl(fallback?.image),
            imageWidth: image ? variant.imageWidth : fallback?.imageWidth,
            imageHeight: image ? variant.imageHeight : fallback?.imageHeight,
          };
        });
        this.families = this.core.buildFamilies(variants, this.config.definitions, this.config.boosterVariants);
        if (!this.families.length) throw new Error('No mapped chair variants are available.');
        const urlVariant = new URL(window.location.href).searchParams.get('variant');
        const initial = this.config.initialVariant;
        this.selection = this.core.getSelection(this.families, urlVariant, false)
          || this.core.getSelection(this.families, initial, false)
          || this.core.getSelection(this.families, this.families.flatMap((family) => family.variants).find((variant) => variant.available)?.id, false)
          || this.core.getSelection(this.families, this.families[0].variants[0].id, false);
        this.formatter = new Intl.NumberFormat(this.config.locale || document.documentElement.lang || 'en-US', {
          style: 'currency', currency: this.config.currency || 'USD',
        });
        this.mediaIndex = 0;
        this.mediaVariantId = null;
        this.productImages = (this.config.images || []).map((image) => ({ ...image, src: imageUrl(image.src) }));
        this.busy = false;
        this.familyButtons = new Map();
        this.colorButtons = new Map();
        this.colorFamilyId = null;
        this.failedPhotos = new Set();
        this.photo = this.querySelector('[data-photo]');
        this.photoSizes = this.config.imageSizes || this.photo?.getAttribute('sizes') || '100vw';
        this.addButton = this.querySelector('[data-add]');
        this.cartStatus = this.querySelector('[data-cart-status]');
        if (!this.photo || !this.addButton || !this.querySelector('[data-fabrics]') || !this.querySelector('[data-colors]')) {
          throw new Error('The configurator markup is incomplete.');
        }
        if (!this.cartStatus) {
          this.cartStatus = make('p', 'oy-config__cart-status');
          this.cartStatus.dataset.cartStatus = '';
          this.cartStatus.setAttribute('role', 'status');
          this.cartStatus.setAttribute('aria-live', 'polite');
          this.addButton.after(this.cartStatus);
        }
        this.renderFamilies();
        this.addEventListener('click', (event) => this.handleClick(event), { signal });
        this.querySelector('[data-gallery-controls]')?.addEventListener('keydown', (event) => {
          if (!this.photos || this.photos.length < 2 || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          if (event.key === 'Home') this.mediaIndex = 0;
          else if (event.key === 'End') this.mediaIndex = Math.max(0, this.photos.length - 1);
          else this.mediaIndex = (this.mediaIndex + (event.key === 'ArrowRight' ? 1 : -1) + this.photos.length) % this.photos.length;
          this.renderMedia();
        }, { signal });
        window.addEventListener('popstate', () => {
          const variant = new URL(window.location.href).searchParams.get('variant');
          if (this.busy) {
            this.pendingVariant = variant;
            return;
          }
          this.chooseVariant(variant || this.config.initialVariant, false);
        }, { signal });
        this.photo.addEventListener('load', () => {
          if (this.photo.naturalWidth > 0 && this.photo.naturalHeight > 0) {
            this.photo.parentElement.style.setProperty('--oy-media-ratio', String(this.photo.naturalWidth / this.photo.naturalHeight));
          }
        }, { signal });
        this.photo.addEventListener('error', () => {
          this.failedPhotos.add(this.photo.src);
          this.renderMedia();
        }, { signal });
        this.render();
        this.querySelectorAll('[data-enhanced]').forEach((element) => { element.hidden = false; });
        this.querySelectorAll('[data-fallback]').forEach((element) => { element.hidden = true; });
        this.ready = true;
        this.syncUrl();
        const dock = this.querySelector('[data-dock]');
        if (dock && 'IntersectionObserver' in window) {
          this.dockObserver = new IntersectionObserver(([entry]) => { dock.hidden = !entry.isIntersecting; });
          this.dockObserver.observe(this);
        }
        if (this.cartAttempted) {
          this.showCartLinks();
          this.cartStatus.textContent = this.text('cartError');
        }
      } catch {
        if (signal.aborted) return;
        this.ready = false;
        this.querySelectorAll('[data-enhanced]').forEach((element) => { element.hidden = true; });
        this.querySelectorAll('[data-fallback]').forEach((element) => { element.hidden = false; });
      }
    }

    text(key, values = {}) {
      const template = typeof this.strings[key] === 'string' ? this.strings[key] : COPY[key] || '';
      return template.replace(/\{(\w+)\}/g, (_, name) => String(values[name] ?? ''));
    }

    money(cents) {
      return cents === null ? this.text('totalUnavailable') : this.formatter.format(cents / 100);
    }

    setText(selector, value) {
      this.querySelectorAll(selector).forEach((element) => { element.textContent = value; });
    }

    renderFamilies() {
      const container = this.querySelector('[data-fabrics]');
      const buttons = this.families.map((family) => {
        const button = make('button', 'oy-config__choice');
        button.type = 'button';
        button.dataset.family = family.id;
        const price = Math.min(...family.variants.map((variant) => variant.price));
        const copy = make('span', 'oy-config__choice-copy');
        copy.append(make('strong', '', family.name), make('small', '', family.description));
        button.append(copy, make('span', 'oy-config__choice-price', this.text('from', { price: this.money(price) })));
        this.familyButtons.set(family.id, button);
        return button;
      });
      container.replaceChildren(...buttons);
    }

    renderColors() {
      const family = this.selection.family;
      if (this.colorFamilyId !== family.id) {
        this.colorFamilyId = family.id;
        this.colorButtons.clear();
        const buttons = family.variants.map((variant) => {
          const button = make('button', 'oy-config__swatch');
          button.type = 'button';
          button.dataset.variant = String(variant.id);
          if (/^#[\da-f]{6}$/i.test(variant.hex)) button.style.setProperty('--swatch', variant.hex);
          button.setAttribute('aria-label', variant.available ? variant.colorName : `${variant.colorName} — ${this.text('soldOut')}`);
          button.title = button.getAttribute('aria-label');
          this.colorButtons.set(variant.id, button);
          return button;
        });
        this.querySelector('[data-colors]').replaceChildren(...buttons);
      }
      family.variants.forEach((variant) => {
        const button = this.colorButtons.get(variant.id);
        button.disabled = this.busy || !variant.available;
        button.setAttribute('aria-pressed', String(variant.id === this.selection.variant.id));
      });
    }

    render() {
      const { family, variant, includeBooster, available, total } = this.selection;
      this.familyButtons.forEach((button, id) => {
        const item = this.families.find((candidate) => candidate.id === id);
        button.disabled = this.busy || !item.variants.some((candidate) => candidate.available);
        button.setAttribute('aria-pressed', String(id === family.id));
      });
      this.renderColors();
      this.querySelectorAll('[data-booster]').forEach((button) => {
        const included = button.dataset.booster === 'true';
        button.disabled = this.busy || (included && !family.booster?.available);
        button.setAttribute('aria-pressed', String(included === includeBooster));
      });
      this.setText('[data-color-name]', variant.colorName);
      this.setText('[data-color-count]', this.text('colorCount', { count: family.variants.length }));
      this.setText('[data-booster-price]', family.booster ? `+ ${this.money(family.booster.price)}` : this.text('unavailable'));
      this.setText('[data-booster-match]', `${family.name} / ${variant.colorName}`);
      this.setText('[data-review-fabric]', family.name);
      this.setText('[data-review-color]', variant.colorName);
      this.setText('[data-review-booster]', includeBooster ? this.text('boosterIncluded') : this.text('seatOnly'));
      this.setText('[data-review-total], [data-summary-total]', this.money(total));
      this.setText('[data-summary-choice]', `${family.name} / ${variant.colorName}${includeBooster ? ` · ${this.text('boosterIncluded')}` : ''}`);
      this.addButton.disabled = this.busy || !available;
      this.addButton.setAttribute('aria-busy', String(this.busy));
      this.addButton.textContent = this.text(this.busy ? 'adding' : available ? 'add' : 'unavailable');
      this.renderMedia();
    }

    handleClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button) || button.disabled || !this.ready) return;
      if (button.hasAttribute('data-family') && !this.busy) {
        const family = this.families.find((candidate) => candidate.id === button.dataset.family);
        const variant = family?.variants.find((candidate) => candidate.available) || family?.variants[0];
        if (variant) this.chooseVariant(variant.id);
      } else if (button.hasAttribute('data-variant') && !this.busy) {
        this.chooseVariant(button.dataset.variant);
      } else if (button.hasAttribute('data-booster') && !this.busy) {
        this.selection = this.core.getSelection(this.families, this.selection.variant.id, button.dataset.booster === 'true');
        this.clearFeedback();
        this.render();
      } else if (button.hasAttribute('data-gallery-step')) {
        if (this.photos.length > 1) {
          this.mediaIndex = (this.mediaIndex + Number(button.dataset.galleryStep) + this.photos.length) % this.photos.length;
          this.renderMedia();
        }
      } else if (button.hasAttribute('data-add')) {
        event.preventDefault();
        this.addToCart();
      }
    }

    chooseVariant(id, updateUrl = true) {
      const next = this.core.getSelection(this.families, id, this.selection.includeBooster);
      if (!next) return;
      this.selection = next;
      this.clearFeedback();
      this.render();
      if (updateUrl) this.syncUrl();
    }

    syncUrl() {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(this.selection.variant.id));
      try { window.history.replaceState(window.history.state, '', url); } catch {}
    }

    clearFeedback() {
      if (!this.cartNeedsCheck) this.cartStatus.textContent = '';
    }

    renderMedia() {
      if (this.mediaVariantId !== this.selection.variant.id) {
        this.mediaVariantId = this.selection.variant.id;
        this.mediaIndex = 0;
        this.gallery = this.core.buildPhotoGallery(this.selection.variant, this.productImages, [
          { src: imageUrl(this.config.roomImage), width: this.config.roomImageWidth, height: this.config.roomImageHeight, kind: 'room' },
        ]);
      }
      this.photos = this.gallery.filter((image) => !this.failedPhotos.has(image.src));
      if (!this.photos.length) {
        const fallback = imageUrl(this.config.defaultImage);
        if (fallback && !this.failedPhotos.has(fallback)) this.photos = [this.core.responsivePhoto({
          src: fallback, width: this.config.defaultImageWidth, height: this.config.defaultImageHeight, kind: 'fallback', alt: '',
        })];
      }
      this.mediaIndex = Math.min(Math.max(this.mediaIndex, 0), Math.max(this.photos.length - 1, 0));
      const image = this.photos[this.mediaIndex];
      this.photo.hidden = !image;
      const ratio = image?.width > 0 && image?.height > 0 ? image.width / image.height : 1;
      this.photo.parentElement.style.setProperty('--oy-media-ratio', String(ratio));
      if (image) {
        this.photo.sizes = this.photoSizes;
        if (this.photo.srcset !== image.srcset) this.photo.srcset = image.srcset;
        if (this.photo.src !== image.src) this.photo.src = image.src;
        for (const dimension of ['width', 'height']) {
          if (Number.isSafeInteger(image[dimension]) && image[dimension] > 0) this.photo.setAttribute(dimension, image[dimension]);
          else this.photo.removeAttribute(dimension);
        }
      } else {
        this.photo.removeAttribute('srcset');
        this.photo.removeAttribute('src');
      }
      const values = { fabric: this.selection.family.name, color: this.selection.variant.colorName };
      const caption = !image ? '' : image.kind === 'selected' ? this.text('productCaption', values)
        : image.kind === 'room' ? this.text('roomCaption') : image.kind === 'fallback' ? this.text('photoFallbackCaption') : this.text('sharedCaption');
      this.photo.alt = !image ? '' : image.kind === 'selected' ? this.text('productAlt', values)
        : image.kind === 'room' ? this.text('roomAlt') : image.kind === 'fallback' ? this.text('photoFallbackAlt') : image.alt || this.text('sharedAlt');
      this.setText('[data-caption]', caption);
      this.setText('[data-photo-position]', image ? this.text('photoPosition', { index: this.mediaIndex + 1, count: this.photos.length }) : '');
      this.setText('[data-photo-status]', image ? '' : this.text('photoUnavailable'));
      this.querySelectorAll('[data-gallery-step]').forEach((button) => { button.disabled = this.photos.length < 2; });
    }

    showCartLinks() {
      this.querySelectorAll('[data-cart-link]').forEach((link) => {
        try {
          const url = new URL(this.config.cartUrl || '/cart', window.location.origin);
          if (url.origin === window.location.origin) link.href = url.href;
        } catch {}
        if (!link.textContent.trim()) link.textContent = this.text('bag');
        link.hidden = false;
      });
    }

    async addToCart() {
      if (this.busy || !this.selection.available) return;
      let items;
      let endpoint;
      try {
        items = this.core.buildCartItems(this.selection, crypto.randomUUID());
        endpoint = new URL(this.config.cartAddUrl || '/cart/add.js', window.location.origin);
        if (endpoint.pathname.endsWith('/cart/add')) endpoint.pathname += '.js';
        if (endpoint.origin !== window.location.origin || !endpoint.pathname.endsWith('/cart/add.js')) throw new Error('Invalid cart endpoint.');
      } catch {
        this.cartStatus.textContent = this.text('unavailable');
        return;
      }
      this.busy = true;
      this.cartStatus.textContent = this.text('adding');
      this.render();
      this.cartAttempted = true;
      this.showCartLinks();
      const signal = this.events.signal;
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
          signal,
        });
        if (!response.ok) throw new Error('Cart submission was not confirmed.');
        const data = await response.json();
        if (!Array.isArray(data.items) || !items.every((item) => data.items.some((added) => Number(added.variant_id ?? added.id) === item.id))) {
          throw new Error('Cart response was incomplete.');
        }
        if (signal.aborted) return;
        this.cartNeedsCheck = false;
        this.cartStatus.textContent = this.text('added');
        document.dispatchEvent(new CustomEvent('oyster:cart-change'));
      } catch {
        this.cartNeedsCheck = true;
        if (!signal.aborted) this.cartStatus.textContent = this.text('cartError');
      } finally {
        if (!signal.aborted) {
          this.busy = false;
          this.render();
          if (this.pendingVariant !== undefined) {
            const variant = this.pendingVariant;
            this.pendingVariant = undefined;
            this.chooseVariant(variant || this.config.initialVariant, false);
          }
        }
      }
    }

    disconnectedCallback() {
      this.connected = false;
      this.ready = false;
      this.events?.abort();
      this.dockObserver?.disconnect();
    }
  });
}
